import { useState, useEffect } from 'react'
import { useEnergyStore } from '@/stores/useEnergyStore'
import { LOCATION_DATA } from '@/data/locationData'

export type SimStatus = 'idle' | 'loading' | 'success' | 'fallback'

// Global cache to prevent multiple simultaneous requests
const cachedData: Record<string, number[]> = {}
const cachedStatus: Record<string, SimStatus> = {}
let fetchPromise: Promise<number[]> | null = null

export const useSimulationData = () => {
  const config = useEnergyStore((s) => s.config)
  const [simulationMonthlyData, setSimulationMonthlyData] = useState<number[]>(cachedData[config.location] || [])
  const [status, setStatus] = useState<SimStatus>(cachedStatus[config.location] || 'idle')

  useEffect(() => {
    let isActive = true

    if (config.hourlySimulation) {
      const loc = LOCATION_DATA[config.location]
      if (!loc) return
      
      if (cachedData[config.location]) {
        if (isActive) {
          setSimulationMonthlyData(cachedData[config.location])
          setStatus(cachedStatus[config.location] || 'success')
        }
        return
      }

      if (isActive) setStatus('loading')

      const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${loc.lat}&longitude=${loc.lng}&start_date=2023-01-01&end_date=2023-12-31&daily=shortwave_radiation_sum&timezone=auto`
      
      if (!fetchPromise) {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 3000)

        fetchPromise = fetch(url, { signal: controller.signal })
          .then(res => res.json())
          .then(data => {
            if (data.daily && data.daily.shortwave_radiation_sum) {
              const monthlySums = new Array(12).fill(0)
              const monthlyCounts = new Array(12).fill(0)
              data.daily.time.forEach((t: string, i: number) => {
                const month = new Date(t).getMonth()
                monthlySums[month] += data.daily.shortwave_radiation_sum[i]
                monthlyCounts[month]++
              })
              const monthlyAvgs = monthlySums.map((sum, i) => sum / (monthlyCounts[i] || 1))
              const yearlyAvg = monthlyAvgs.reduce((a, b) => a + b, 0) / 12
              const adjustments = monthlyAvgs.map(avg => ((avg / yearlyAvg) - 1) * 100)
              cachedData[config.location] = adjustments
              cachedStatus[config.location] = 'success'
              return adjustments
            }
            throw new Error('Invalid data')
          })
          .catch(() => {
            // Synthetic Southern Hemisphere solar irradiance variance (fallback if API offline)
            // Must sum to 0 so annual generation is unbiased vs. the trig seasonal curve
            const fallback = [47, 36, 11, -19, -39, -58, -54, -29, 2, 21, 36, 46]
            cachedData[config.location] = fallback
            cachedStatus[config.location] = 'fallback'
            return fallback
          })
          .finally(() => {
            clearTimeout(timeoutId)
            fetchPromise = null
          })
      }

      fetchPromise.then(data => {
        if (isActive) {
          setSimulationMonthlyData(data)
          setStatus(cachedStatus[config.location] || 'success')
        }
      })
    } else {
      if (isActive) {
        setSimulationMonthlyData([])
        setStatus('idle')
      }
    }

    return () => {
      isActive = false
    }
  }, [config.hourlySimulation, config.location])

  return { data: simulationMonthlyData, status }
}
